import React, { useEffect } from "react";
import styled from "styled-components";

import { useTranslate } from '@tolgee/react';
import TimePicker from "./TimePicker";
import MeetingDateTimePicker from "./MeetingDateTimePicker";
import RecurrenceFieldsCard from "./RecurrenceFieldsCard";
import { ReactComponent as RightArrow } from "../../Assets/icons/rightArrow.svg";
import { ReactComponent as CloseBtn } from "../../Assets/iconsv2/generic/closeBtn.svg";

import { cloneDeep } from "lodash";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { ADD_MEMBERS } from "../../Constants/constantValues";

const NewMeetingDetails = ({
  handleInputChange,
  inputValues,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  recurrencePattern,
  setRecurrencePattern,
  selectedContacts,
  setAddParticipants,
  handleCreateNewMeeting,
  isSubmitting,
  otherParticipants,
  handleCloseAddNewMeeting,
}) => {
  const selectedNames = selectedContacts.map((contact) => contact.name);
  const allParticipants = selectedNames.concat(otherParticipants);

  const now = new Date();
  const cloneStart = cloneDeep(startDate);
  const isToday = cloneStart.toDateString() === now.toDateString();
  const minTime = isToday ? now : new Date(cloneStart.setHours(0, 0, 0, 0));

  const calculateMinEndTime = () => {
    if (endDate.toDateString() === cloneStart.toDateString()) {
      const strictMinTime = new Date(cloneStart);
      strictMinTime.setMinutes(strictMinTime.getMinutes() + 1); 
      return strictMinTime;
    }
    return null; // No constraint if the end date is on a different day
  };

  const minEndDateTime =
    endDate.toDateString() === startDate.toDateString()
      ? new Date(Math.max(startDate.getTime(), now.getTime()))
      : now;

  function auto_grow(element) {
    element.style.height = "5px";
    element.style.height = element.scrollHeight + "px";
  }

  useEffect(() => {
    let element = document.getElementById("resizeTa-3");
    auto_grow(element);
  }, [inputValues]);
  const { t } = useTranslate();
  return (
    <Container>
      <Header>
        <TitleWrapper>
          <Title>{t('new_chat.create_meeting')}</Title>
        </TitleWrapper>
        <CloseButton onClick={() => handleCloseAddNewMeeting()}>
          <CloseBtn />
        </CloseButton>
      </Header>
      <CardInputsWrapper>
        <Group>
          <OptionWrapper>
            <CustomNameInput
              value={inputValues.meetingTitle}
              placeholder={t('new_chat.title')}
              onChange={(e) => handleInputChange(e)}
              name="meetingTitle"
              maxLength={100}
            ></CustomNameInput>
            <Separator />
            <CustomTextArea
              id="resizeTa-3"
              value={inputValues.meetingDetails}
              placeholder={t('meetings.description')}
              onChange={(e) => handleInputChange(e)}
              name="meetingDetails"
              maxLength={1000}
            ></CustomTextArea>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <DateTimeWrapper>
              <DateInfo>{t('new_chat.starts')} </DateInfo>
              <MeetingDateTimePicker
                slim={true}
                format={"dd-MM-yyyy"}
                value={startDate}
                setValue={setStartDate}
                noCalendar
                id={"start_time_date"}
              />
              <TimePicker
                date={startDate}
                setDate={setStartDate}
                id={"start_time"}
                minTime={minTime}
                minDateTime={now}
              />
            </DateTimeWrapper>
            <Separator />
            <DateTimeWrapper>
              <DateInfo>{t('new_chat.ends')}</DateInfo>
              <MeetingDateTimePicker
                slim={true}
                format={"dd-MM-yyyy"}
                value={endDate}
                setValue={setEndDate}
                noCalendar
                id={"end_time_date"}
              />
              <TimePicker
                date={endDate}
                setDate={setEndDate}
                minTime={calculateMinEndTime()}
                minDateTime={minEndDateTime}
                id={"end_time"}
              />
            </DateTimeWrapper>
          </OptionWrapper>
        </Group>
        <Group>
          <RecurrenceFieldsCard
            startDate={startDate}
            pattern={recurrencePattern}
            onChange={setRecurrencePattern}
          />
        </Group>
        <Group>
          <OptionWrapper
            onClick={() => setAddParticipants(true)}
            style={{ cursor: "pointer", overflow: "auto" }}
          >
            <InvitesButton>
              {t('meetings.invitees')}
              <p>{selectedContacts.length + otherParticipants.length}</p>
              <RightArrow />
            </InvitesButton>
            {allParticipants.length > 0 && (
              <ParticipantsContainer>
                {allParticipants.map((item, index) => (
                  <InvitedPeople key={index}>
                    <p>{item}</p>
                  </InvitedPeople>
                ))}
              </ParticipantsContainer>
            )}
          </OptionWrapper>
        </Group>
        <Group>
          <PrimaryButton
            style={{ padding: "16px", width: "100%" }}
            disabled={
              isSubmitting ||
              inputValues.meetingTitle.length <= 2 ||
              (selectedContacts?.length == 0 && otherParticipants?.length == 0)
            }
            onClick={() => handleCreateNewMeeting()}
          >
            {t('new_chat.create_meeting')}
          </PrimaryButton>
        </Group>
      </CardInputsWrapper>
    </Container>
  );
};

export default NewMeetingDetails;
const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  gap: 25px;
  background: ${({ theme }) => theme.chat.conversationBackground};
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Title = styled.p`
 text-transform: capitalize;
   font-weight: 700;
font-size: 20px;
line-height:32px;
letter-spacing: 0.04px;
color:${({ theme }) => theme.menu.primaryTextColor};
`;

const CardInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 0 auto;
  padding:0 21px;
  width: 100%;
  max-width:631px;
  overflow: auto;
`;

const Group = styled.div`
  display: flex;
  border-radius: 4px;
  align-items: center;
  width: 100%;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 4px;
  width: 100%;
`;

const CustomInput = styled.input`
  background: none;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.40799999237060547px;
  width: 100%;
  padding: 15px 16px;
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;

const CustomNameInput = styled(CustomInput)`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;
const DateTimeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
`;

const DateInfo = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const InvitesButton = styled.button`
  display: flex;
  align-items: center;
  border-radius: 5px;
  border: none;
  padding: 14px 16px;
  cursor: pointer;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  font-size: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  gap: 10px;
  p {
    margin-left: auto;
  }
`;

const ParticipantsContainer = styled.div`
  padding-bottom: 8px;
`;

const InvitedPeople = styled.div`
  overflow: hidden;
  display: flex;
  width: 100%;
  padding: 4px 16px;
  p {
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: left;
  }
`;
const Separator = styled.div`
  width: 100%;
  min-height: 1px;
  background: ${({ theme }) => theme.sidebar.borderColor};
`;

const CustomTextArea = styled.textarea`
  padding: 15px 16px;
  width: 100%;
  resize: none;
  background: none;
  min-height: 80px;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  max-height: 250px;
  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;
const CloseButton = styled.button`
border:none;
background:none;
cursor:pointer;
display:flex;
align-items:center;
justify-content:center;

svg{
height:26px;
width:26px;
color:${({ theme }) => theme.menu.selectedOpionBackground};
stroke:${({ theme }) => theme.sidebar.borderColor};
}
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.chat.conversationBackground};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;

